import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBase } from '../apiBase';
import { getUsername, authFetch, isCollector, isManagerOrAdmin } from '../auth';
import {
  LoadingSpinner,
  TableFiltersBar,
  TablePaginationBar,
  filterControl,
  filterLabel,
  filterLabelNarrow,
  mobileCardList,
  MobileRowCard,
  rowMatchesQuery,
  scrollTableWrap,
  stickyFirstTd,
  stickyFirstTh,
  stickyThead,
  useTablePagination,
  modalPanelClass,
} from './tableToolbar';
import { detailRowAttrs } from './RowDetailModal';
import MonthlyTargetProgressBar from './MonthlyTargetProgressBar';
import { CollectorSelectField, useCollectors } from './useCollectors';

const apiBase = getApiBase();

const emptyForm = () => ({
  id: '',
  name: '',
  location: '',
  contactNumber: '',
  email: '',
  ownerName: '',
  ownerBirthday: '',
  pastBill: '',
  collectorUserId: '',
});

/** Increment trailing digits in an ID (e.g. `42` → `43`, `C001` → `C002`). */
function incrementCustomerId(id) {
  const raw = String(id ?? '').trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) {
    return String(parseInt(raw, 10) + 1);
  }
  const m = raw.match(/^(.+?)(\d+)$/);
  if (!m) return null;
  const prefix = m[1];
  const numStr = m[2];
  const next = parseInt(numStr, 10) + 1;
  return prefix + String(next).padStart(numStr.length, '0');
}

function maxNumericSuffixFromIds(customers) {
  let max = 0;
  for (const c of customers) {
    const raw = String(c?.id ?? '').trim();
    if (!raw) continue;
    if (/^\d+$/.test(raw)) {
      max = Math.max(max, parseInt(raw, 10));
      continue;
    }
    const m = raw.match(/(\d+)$/);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return max;
}

/** Suggest the next ID after the highest number already used by any user. */
function suggestNextCustomerId(customers) {
  const list = Array.isArray(customers) ? customers : [];
  let best = null;
  for (const c of list) {
    const raw = String(c?.id ?? '').trim();
    const match = raw.match(/^(.*?)(\d+)$/);
    if (!match) continue;
    const num = parseInt(match[2], 10);
    if (!Number.isFinite(num)) continue;
    if (!best || num > best.num) {
      best = { raw, prefix: match[1], num, width: match[2].length };
    }
  }
  if (best) {
    const next = incrementCustomerId(best.raw);
    if (next) return next;
  }
  return String(maxNumericSuffixFromIds(list) + 1);
}

function money(n) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'LKR',
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function todayYmdLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function amountToPayOf(row) {
  return Math.max(0, Number(row?.remainingAmount) || 0);
}

/** Past due date only counts while something is still owed. */
function isCustomerOverdue(row, today) {
  return amountToPayOf(row) > 0 && Boolean(row?.dueDate && row.dueDate < today);
}

function isCustomerSettled(row) {
  return amountToPayOf(row) === 0;
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [search, setSearch] = useState('');
  const [dueFilter, setDueFilter] = useState('all');
  const { collectors, loading: collectorsLoading } = useCollectors();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${apiBase}/api/customers`);
      if (!res.ok) throw new Error('Failed to load customers');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Could not load data');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    load();
  }, [load]);

  const today = useMemo(() => todayYmdLocal(), []);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const overdue = isCustomerOverdue(r, today);
      if (dueFilter === 'overdue' && !overdue) return false;
      if (dueFilter === 'current' && overdue) return false;
      return rowMatchesQuery(search, [
        r.id,
        r.name,
        r.location,
        r.contactNumber,
        r.email,
        r.ownerName,
        r.collectorName,
        r.dueDate,
        String(r.remainingAmount ?? ''),
      ]);
    });
  }, [rows, search, dueFilter, today]);

  const pagination = useTablePagination(filteredRows.length, [search, dueFilter]);
  const pagedRows = useMemo(
    () => filteredRows.slice(pagination.offset, pagination.offset + pagination.pageSize),
    [filteredRows, pagination.offset, pagination.pageSize]
  );

  const openCustomerProfile = (customerId) => {
    navigate(`/dashboard/customers/${encodeURIComponent(customerId)}`);
  };

  const openModal = async () => {
    let nextId = suggestNextCustomerId(rows);
    try {
      const res = await authFetch(`${apiBase}/api/customers/next-id`);
      if (res.ok) {
        const data = await res.json();
        const fromAllUsers = String(data?.id ?? '').trim();
        if (fromAllUsers) nextId = fromAllUsers;
      }
    } catch {
      /* fall back to the customers this user can see */
    }
    setForm({ ...emptyForm(), id: nextId });
    setSaveError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSaveError(null);
  };

  const handleFormChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const username = getUsername();
    if (!username) {
      setSaveError('You need to be signed in with a username.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (isManagerOrAdmin() && !form.collectorUserId.trim()) {
        setSaveError('Select an assigned collector.');
        setSaving(false);
        return;
      }
      const payload = {
          addedBy: username,
          id: form.id.trim(),
          name: form.name.trim(),
          location: form.location.trim(),
          contactNumber: form.contactNumber.trim(),
          email: form.email.trim(),
          ownerName: form.ownerName.trim(),
          ownerBirthday: form.ownerBirthday.trim(),
          pastBill: form.pastBill,
        };
      if (isManagerOrAdmin()) {
        payload.collectorUserId = form.collectorUserId.trim();
      }
      const res = await authFetch(`${apiBase}/api/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(data.error || 'Save failed');
        return;
      }
      await load();
      closeModal();
    } catch {
      setSaveError('Could not reach the server.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500">
          {isCollector()
            ? 'View customer profiles and account balances assigned to you.'
            : 'Manage customers, opening balances, and amounts owed.'}
        </p>
        {isManagerOrAdmin() ? (
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={openModal}
            className="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:brightness-[1.03] sm:w-auto"
          >
            Add customer
          </button>
        </div>
        ) : null}
      </div>

      {error ? (
        <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-800 ring-1 ring-red-100" role="alert">
          {error}
        </p>
      ) : null}

      <TableFiltersBar
        hint={
          !loading && rows.length > 0
            ? `Showing ${filteredRows.length} of ${rows.length} customer${rows.length === 1 ? '' : 's'}`
            : null
        }
      >
        <label className={filterLabel}>
          Search
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, ID, location, phone, email, due date…"
            className={filterControl}
          />
        </label>
        <label className={filterLabelNarrow}>
          Due status
          <select
            value={dueFilter}
            onChange={(e) => setDueFilter(e.target.value)}
            className={filterControl}
          >
            <option value="all">All</option>
            <option value="overdue">Overdue only</option>
            <option value="current">Not overdue</option>
          </select>
        </label>
      </TableFiltersBar>

      <div className="space-y-3">
      <div className={mobileCardList}>
        {loading ? (
          <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
            <LoadingSpinner />
          </p>
        ) : rows.length === 0 ? (
          <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
            No customers yet.{isManagerOrAdmin() ? ' Use "Add customer" to create a record.' : ' None assigned to you yet.'}
          </p>
        ) : filteredRows.length === 0 ? (
          <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
            No customers match your search or filters.
          </p>
        ) : (
          pagedRows.map((r) => {
            const overdue = isCustomerOverdue(r, today);
            const settled = isCustomerSettled(r);
            return (
              <MobileRowCard
                key={r.id}
                title={r.name}
                subtitle={
                  [r.location, r.contactNumber, r.id ? `ID ${r.id}` : '', r.email]
                    .filter(Boolean)
                    .join(' · ') || undefined
                }
                onClick={() => openCustomerProfile(r.id)}
                badge={
                  overdue ? (
                    <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-800">
                      Overdue
                    </span>
                  ) : settled ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800">
                      Settled
                    </span>
                  ) : null
                }
                fields={[
                  { label: 'Remaining', value: money(r.remainingAmount) },
                  ...(Number(r.monthlyTargetBags) > 0
                    ? [
                        {
                          label: 'Monthly target',
                          value: (
                            <MonthlyTargetProgressBar
                              compact
                              sold={r.monthlyBagsSold}
                              target={r.monthlyTargetBags}
                              progressPct={r.monthlyTargetProgressPct}
                            />
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            );
          })
        )}
      </div>
      <div className={`hidden sm:block ${scrollTableWrap}`}>
        <table className="w-full min-w-[720px] data-table border-separate border-spacing-0 text-left text-sm">
          <thead className={stickyThead}>
            <tr className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className={`px-4 py-3 ${stickyFirstTh}`}>Customer name</th>
              <th className="min-w-[10rem] px-4 py-3">Monthly target</th>
              <th className="px-4 py-3 text-right">Remaining amount</th>
              <th className="whitespace-nowrap px-4 py-3 text-center"> </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-800">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-slate-500">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-slate-500">
                  No customers yet.{isManagerOrAdmin() ? ' Use "Add customer" to create a record.' : ' None assigned to you yet.'}
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-slate-500">
                  No customers match your search or filters.
                </td>
              </tr>
            ) : (
              pagedRows.map((r) => {
                const overdue = isCustomerOverdue(r, today);
                const settled = isCustomerSettled(r);
                return (
                  <tr
                    key={r.id}
                    {...detailRowAttrs(
                      () => openCustomerProfile(r.id),
                      overdue ? 'bg-rose-50/50 hover:bg-rose-50/80' : 'hover:bg-slate-50/80',
                    )}
                    title="View customer profile"
                    aria-label={`Customer ${r.name || ''}`}
                  >
                    <td className={`px-4 py-3 ${stickyFirstTd}`}>
                      <p className="font-semibold text-slate-900">{r.name}</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {r.location}
                        {r.contactNumber ? ` · ${r.contactNumber}` : ''}
                        {r.id ? ` · ID ${r.id}` : ''}
                        {r.email ? ` · ${r.email}` : ''}
                      </p>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <MonthlyTargetProgressBar
                        compact
                        sold={r.monthlyBagsSold}
                        target={r.monthlyTargetBags}
                        progressPct={r.monthlyTargetProgressPct}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {settled ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800">
                            Settled
                          </span>
                        ) : null}
                        <span
                          className={`font-semibold tabular-nums ${
                            overdue ? 'text-rose-800' : 'text-slate-900'
                          }`}
                        >
                          {money(r.remainingAmount)}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCustomerProfile(r.id);
                        }}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-800"
                      >
                        View account
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {!loading && rows.length > 0 ? (
        <TablePaginationBar
          page={pagination.page}
          totalPages={pagination.totalPages}
          pageSize={pagination.pageSize}
          totalCount={filteredRows.length}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
        />
      ) : null}
      </div>

      {modalOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="customers-modal-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            aria-label="Close"
            onClick={closeModal}
          />
          <div className={modalPanelClass}>
            <h2 id="customers-modal-title" className="text-lg font-bold text-slate-900">
              Add customer
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Recorded as user: {getUsername() || '—'}. Past bill sets the opening balance to collect.
            </p>
            <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
              {saveError ? (
                <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-100">
                  {saveError}
                </p>
              ) : null}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-slate-600 sm:col-span-2">
                Customer ID
                <input
                  type="text"
                  required
                  value={form.id}
                  onChange={(e) => handleFormChange('id', e.target.value)}
                  className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 font-mono text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                  placeholder="e.g. 1"
                  autoComplete="off"
                />
                <span className="mt-1 block text-xs font-normal text-slate-500">
                  Pre-filled from the last added customer; you can change it. Must be unique.
                </span>
              </label>
              <label className="block text-sm font-medium text-slate-600 sm:col-span-2">
                Customer name
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                  placeholder="e.g. Perera Hardware"
                  autoComplete="organization"
                />
              </label>
              <label className="block text-sm font-medium text-slate-600">
                Location
                <input
                  type="text"
                  required
                  value={form.location}
                  onChange={(e) => handleFormChange('location', e.target.value)}
                  className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                  placeholder="Town or address"
                  autoComplete="street-address"
                />
              </label>
              <label className="block text-sm font-medium text-slate-600">
                Contact number
                <input
                  type="tel"
                  required
                  value={form.contactNumber}
                  onChange={(e) => handleFormChange('contactNumber', e.target.value)}
                  className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                  placeholder="e.g. 077 123 4567"
                  autoComplete="tel"
                />
              </label>
              <label className="block text-sm font-medium text-slate-600 sm:col-span-2">
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                  placeholder="e.g. shop@example.com"
                  autoComplete="email"
                />
              </label>
              <label className="block text-sm font-medium text-slate-600">
                Shop owner name
                <input
                  type="text"
                  value={form.ownerName}
                  onChange={(e) => handleFormChange('ownerName', e.target.value)}
                  className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                  placeholder="e.g. Sunil Perera"
                  autoComplete="name"
                />
              </label>
              <label className="block text-sm font-medium text-slate-600">
                Owner birthday
                <input
                  type="date"
                  value={form.ownerBirthday}
                  onChange={(e) => handleFormChange('ownerBirthday', e.target.value)}
                  max={today}
                  className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                />
                <span className="mt-1 block text-xs font-normal text-slate-500">
                  Optional. Shown on Analytics when the birthday is today or in the next 3 days.
                </span>
              </label>
              {isManagerOrAdmin() ? (
                <CollectorSelectField
                  id="new-customer-collector"
                  value={form.collectorUserId}
                  onChange={(v) => handleFormChange('collectorUserId', v)}
                  disabled={saving}
                  collectors={collectors}
                  loading={collectorsLoading}
                  allowEmpty={false}
                  required
                />
              ) : null}
              <label className="block text-sm font-medium text-slate-600 sm:col-span-2">
                Past bill (amount to be paid)
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  required
                  value={form.pastBill}
                  onChange={(e) => handleFormChange('pastBill', e.target.value)}
                  className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm tabular-nums ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                  placeholder="0.00"
                />
              </label>
              </div>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md disabled:opacity-60"
                >
                  {saving ? 'Saving…' : 'Save customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
